import type { SiteManifest } from './types';

/**
 * CloudMarket Depot site-content manifest. Headlines and shopping copy come from
 * the approved Claude Design canvas and the client's previous template; the
 * welcome line, tagline and footer blurb are that template's own words.
 *
 * Nothing about the company is invented here. The address, phone, email, hours,
 * registration number, story, mission, vision and every photo default to EMPTY,
 * and the pages hide whatever is still empty, so a fresh site shows no placeholder
 * text. The client fills them in Admin > Storefront > Site Pages.
 *
 * Group ids are `cm-*` because the manifest lint forbids any field path that
 * another shipped manifest already claims.
 *
 * NOT here (already runtime-editable elsewhere): the social links (storefront
 * `social`), brand names (`brand`), nav labels/routes and colors/fonts.
 */
export const CLOUDMARKET_SITE: SiteManifest = {
  tenant: 'cloudmarket',
  label: 'CloudMarket Depot Website',
  // `/` is the bespoke landing and /storefront redirects to /shop, so the shared
  // Home tab, Official Store and Featured Packages never render. Landing Packages
  // do: they fill the landing's Packages section.
  adminHide: {
    storefrontTabs: ['home', 'official'],
    sidebarItems: ['/featured-packages'],
  },
  groups: [
    {
      id: 'cm-home',
      label: 'Home',
      sections: [
        {
          id: 'hero',
          label: 'Welcome banner',
          fields: [
            {
              id: 'eyebrow',
              kind: 'text',
              label: 'Small line above the headline',
              default: 'Fuel your day, the healthy way',
            },
            {
              id: 'title',
              kind: 'text',
              label: 'Headline',
              default: 'Welcome to CloudMarket Depot',
            },
            {
              id: 'body',
              kind: 'textarea',
              label: 'Text',
              default:
                'Discover premium products designed to support a healthier home, workspace, and lifestyle.',
            },
            { id: 'shopCta', kind: 'text', label: 'Shop button', default: 'Shop Now' },
            {
              id: 'joinCta',
              kind: 'text',
              label: 'Join button (visitors only)',
              default: 'Join Membership',
            },
            {
              id: 'slides',
              kind: 'objectList',
              label: 'Banner photos',
              itemFields: [
                { id: 'imageKey', kind: 'image', label: 'Photo' },
                { id: 'alt', kind: 'text', label: 'What the photo shows' },
              ],
              default: [],
              maxItems: 6,
            },
          ],
        },
        {
          id: 'tiles',
          label: 'Tiles beside the banner',
          fields: [
            { id: 'joinEyebrow', kind: 'text', label: 'Gold tile: label', default: 'Membership' },
            {
              id: 'joinTitle',
              kind: 'text',
              label: 'Gold tile: heading',
              default: 'Join with a package',
            },
            {
              id: 'joinBody',
              kind: 'textarea',
              label: 'Gold tile: text',
              default: 'Members shop at member prices and earn PV on qualifying orders.',
            },
            { id: 'joinCta', kind: 'text', label: 'Gold tile: link', default: 'See packages' },
            { id: 'payEyebrow', kind: 'text', label: 'Blue tile: label', default: 'Payments' },
            { id: 'payTitle', kind: 'text', label: 'Blue tile: heading', default: 'Pay your way' },
            {
              id: 'payBody',
              kind: 'textarea',
              label: 'Blue tile: text',
              default: 'GCash, Maya, bank transfer or cash on delivery.',
            },
            {
              id: 'payCta',
              kind: 'text',
              label: 'Blue tile: link',
              default: 'How ordering works',
            },
          ],
        },
        {
          id: 'trust',
          label: 'Shopping with us strip',
          fields: [
            {
              id: 'items',
              kind: 'objectList',
              label: 'Items',
              fixedLength: true,
              itemFields: [
                { id: 'title', kind: 'text', label: 'Heading' },
                { id: 'text', kind: 'text', label: 'Text' },
              ],
              default: [
                { title: 'Cash on delivery', text: 'Pay when your order arrives.' },
                { title: 'GCash and Maya', text: 'Or pay by bank transfer.' },
                { title: 'Track every order', text: 'From packed to delivered.' },
                { title: 'Member prices', text: 'Plus PV on qualifying orders.' },
              ],
            },
          ],
        },
        {
          id: 'sections',
          label: 'Section headings',
          hint: 'Categories, packages and products come from the catalog. A section stays hidden until it has something to show.',
          fields: [
            {
              id: 'categories',
              kind: 'text',
              label: 'Categories heading',
              default: 'Shop by category',
            },
            { id: 'viewAll', kind: 'text', label: 'View all link', default: 'View all' },
            { id: 'packages', kind: 'text', label: 'Packages heading', default: 'Packages' },
            {
              id: 'packagesBody',
              kind: 'textarea',
              label: 'Packages text',
              default:
                'Buy a package to become a member. It activates your account and unlocks member prices.',
            },
            {
              id: 'packageCta',
              kind: 'text',
              label: 'Package button',
              default: 'Join with this package',
            },
            {
              id: 'featured',
              kind: 'text',
              label: 'Featured products heading',
              default: 'Featured products',
            },
            { id: 'all', kind: 'text', label: 'All products heading', default: 'All products' },
            {
              id: 'allCta',
              kind: 'text',
              label: 'Button under all products',
              default: 'View all products',
            },
          ],
        },
        {
          id: 'newsletter',
          label: 'Newsletter band',
          hint: 'Sign-ups land in the admin contact inbox.',
          fields: [
            {
              id: 'title',
              kind: 'text',
              label: 'Heading',
              default: 'New arrivals and promos, in your inbox',
            },
            {
              id: 'body',
              kind: 'text',
              label: 'Text',
              default: 'Product news from CloudMarket Depot. Unsubscribe anytime.',
            },
            { id: 'button', kind: 'text', label: 'Button', default: 'Subscribe' },
            {
              id: 'sent',
              kind: 'text',
              label: 'Message after signing up',
              default: 'Thanks! You are on the list.',
            },
          ],
        },
      ],
    },
    {
      id: 'cm-about',
      label: 'About Us',
      sections: [
        {
          id: 'hero',
          label: 'Top of the page',
          fields: [
            { id: 'title', kind: 'text', label: 'Heading', default: 'About CloudMarket Depot' },
            {
              id: 'intro',
              kind: 'textarea',
              label: 'Who you are, what you sell and who you serve',
              default: '',
            },
            {
              id: 'photo',
              kind: 'objectList',
              label: 'Photo',
              itemFields: [
                { id: 'imageKey', kind: 'image', label: 'Team, store or product photo' },
                { id: 'alt', kind: 'text', label: 'What the photo shows' },
              ],
              default: [],
              maxItems: 1,
            },
          ],
        },
        {
          id: 'story',
          label: 'Our story',
          hint: 'Hidden until the story has text.',
          fields: [
            { id: 'eyebrow', kind: 'text', label: 'Label', default: 'Our story' },
            { id: 'title', kind: 'text', label: 'Heading', default: '' },
            {
              id: 'paras',
              kind: 'richParas',
              label: 'Story',
              default: [],
              hint: 'When and why you started, where you are based and how you deliver.',
            },
            {
              id: 'photo',
              kind: 'objectList',
              label: 'Photo',
              itemFields: [
                { id: 'imageKey', kind: 'image', label: 'Photo for your story' },
                { id: 'alt', kind: 'text', label: 'What the photo shows' },
              ],
              default: [],
              maxItems: 1,
            },
          ],
        },
        {
          id: 'purpose',
          label: 'Mission and vision',
          hint: 'Each card stays hidden until it has text.',
          fields: [
            { id: 'missionTitle', kind: 'text', label: 'Mission heading', default: 'Our mission' },
            { id: 'mission', kind: 'textarea', label: 'Mission statement', default: '' },
            { id: 'visionTitle', kind: 'text', label: 'Vision heading', default: 'Our vision' },
            { id: 'vision', kind: 'textarea', label: 'Vision statement', default: '' },
          ],
        },
        {
          id: 'sell',
          label: 'What we sell',
          hint: 'The cards are your catalog categories.',
          fields: [
            { id: 'eyebrow', kind: 'text', label: 'Label', default: 'What we sell' },
            {
              id: 'title',
              kind: 'text',
              label: 'Heading',
              default: 'Products for home, work and everyday wellness',
            },
          ],
        },
        {
          id: 'why',
          label: 'Why shop with us',
          fields: [
            { id: 'eyebrow', kind: 'text', label: 'Label', default: 'Why shop with us' },
            {
              id: 'title',
              kind: 'text',
              label: 'Heading',
              default: 'Simple to order, easy to pay',
            },
            {
              id: 'items',
              kind: 'objectList',
              label: 'Cards',
              fixedLength: true,
              itemFields: [
                { id: 'title', kind: 'text', label: 'Heading' },
                { id: 'text', kind: 'textarea', label: 'Text' },
              ],
              default: [
                {
                  title: 'Member prices',
                  text: 'Members buy at member prices and earn PV on qualifying orders.',
                },
                { title: 'Pay your way', text: 'GCash, Maya, bank transfer or cash on delivery.' },
                {
                  title: 'Track every order',
                  text: 'See your order status from packed to delivered.',
                },
                { title: 'Real support', text: 'Send us a message from our contact page.' },
              ],
            },
          ],
        },
        {
          id: 'join',
          label: 'Membership band',
          fields: [
            {
              id: 'title',
              kind: 'text',
              label: 'Heading',
              default: 'Become a CloudMarket member',
            },
            {
              id: 'body',
              kind: 'text',
              label: 'Text',
              default: 'Pick a package, activate your account, and shop at member prices.',
            },
            { id: 'packagesCta', kind: 'text', label: 'First button', default: 'See packages' },
            {
              id: 'signupCta',
              kind: 'text',
              label: 'Second button (visitors only)',
              default: 'Create an account',
            },
          ],
        },
      ],
    },
    {
      id: 'cm-contact',
      label: 'Contact',
      sections: [
        {
          id: 'hero',
          label: 'Top of the page',
          fields: [
            { id: 'title', kind: 'text', label: 'Heading', default: "We're happy to help" },
            {
              id: 'body',
              kind: 'textarea',
              label: 'Text',
              default: 'Questions about an order, a package or your membership? Reach us here.',
            },
          ],
        },
        {
          id: 'details',
          label: 'Contact details',
          hint: 'Also shown in the footer. Empty lines stay hidden.',
          fields: [
            { id: 'address', kind: 'textarea', label: 'Office address', default: '' },
            { id: 'landline', kind: 'text', label: 'Landline', default: '' },
            { id: 'mobile', kind: 'text', label: 'Mobile number', default: '' },
            { id: 'email', kind: 'text', label: 'Email address', default: '' },
            { id: 'hours', kind: 'textarea', label: 'Business hours', default: '' },
            {
              id: 'mapEmbed',
              kind: 'text',
              label: 'Google Maps embed link',
              default: '',
              hint: 'In Google Maps: Share, Embed a map, then copy only the https://www.google.com/maps/embed link.',
            },
          ],
        },
        {
          id: 'form',
          label: 'Message form',
          hint: 'Messages land in the admin contact inbox.',
          fields: [
            { id: 'title', kind: 'text', label: 'Heading', default: 'Send us a message' },
            {
              id: 'note',
              kind: 'text',
              label: 'Reply time note',
              default: '',
              hint: 'For example: We reply within one business day.',
            },
            {
              id: 'topics',
              kind: 'stringList',
              label: 'Topics',
              default: [
                'An order I placed',
                'Packages and membership',
                'Payments',
                'Something else',
              ],
              maxItems: 8,
            },
            { id: 'button', kind: 'text', label: 'Button', default: 'Send message' },
            {
              id: 'sentTitle',
              kind: 'text',
              label: 'Message after sending: heading',
              default: 'Message sent',
            },
            {
              id: 'sentBody',
              kind: 'text',
              label: 'Message after sending: text',
              default: 'Thank you. We will get back to you soon.',
            },
          ],
        },
      ],
    },
    {
      id: 'cm-chrome',
      label: 'Header and Footer',
      sections: [
        {
          id: 'header',
          label: 'Header',
          fields: [
            {
              id: 'logo',
              kind: 'image',
              label: 'Header logo',
              defaultUrl: '/tenants/cloudmarket/logo-lockup.png',
            },
            {
              id: 'tagline',
              kind: 'text',
              label: 'Top bar tagline',
              default: 'Fuel Your Day, the Healthy Way.',
            },
          ],
        },
        {
          id: 'footer',
          label: 'Footer',
          fields: [
            {
              id: 'logo',
              kind: 'image',
              label: 'Footer logo',
              defaultUrl: '/tenants/cloudmarket/logo-lockup.png',
            },
            {
              id: 'blurb',
              kind: 'textarea',
              label: 'About line',
              default:
                "Your trusted marketplace for quality products. From home essentials to business solutions, we've got you covered.",
            },
            { id: 'payTitle', kind: 'text', label: 'Payments label', default: 'We accept' },
            {
              id: 'payments',
              kind: 'stringList',
              label: 'Payment methods',
              default: ['GCash', 'Maya', 'Bank transfer', 'Cash on delivery'],
              maxItems: 8,
            },
            {
              id: 'copyright',
              kind: 'text',
              label: 'Copyright line',
              default: 'CloudMarket Depot. All rights reserved.',
            },
            { id: 'registration', kind: 'text', label: 'DTI / SEC registration', default: '' },
          ],
        },
      ],
    },
  ],
};
